/**
 * The GM's **control-assignment** command builders — the browser's reach for `set_controller`
 * (re-homing a unit's owner of record, or granting a player CTRL and/or EYES on it).
 *
 * Pure builders, no React — the same exported-for-testability discipline `disclosure.ts` uses,
 * so the shapes are proven by applying them through the **real** `applyEncounter` reducer
 * (`controlAssignment.test.ts`) rather than against hand-built literals.
 *
 * **`set_controller` replaces each grant list wholesale**, so the add/remove gestures a UI wants
 * are a read-modify-write against the combatant in hand — exactly what the CLI's `grant`/`revoke`
 * do against their pre-flight read. The reducer normalizes what it stores (dedupe; the co-list also
 * drops the owner of record), and these builders deliberately do NOT re-implement that: a builder that quietly normalized too would be a second copy of an invariant
 * that already has one home.
 */
import { controllersOf, type CombatantId, type Combatant, type SetControllerCommand, type Side } from "@lancer-vtt/domain";

/**
 * Reassign the **owner of record** (a re-home), leaving every grant exactly as it is.
 *
 * Omitting both grant lists means "leave alone" — grants survive a change of owner.
 * That is the primitive's documented behavior and the right default here: the GM is moving one
 * seat, not disbanding the crew. (The CLI guards this gesture behind an explicit
 * `--keep-controllers`/`--clear-controllers` because a shell invocation gives no feedback about
 * what it left standing; a UI needs no such guard because the grant chips are *right there*,
 * still lit, in the same panel.)
 */
export function setOwnerCommand(args: {
  actorId: string;
  combatantId: CombatantId;
  controllerSlotId: string | null;
  allegiance?: Side;
}): SetControllerCommand {
  const { actorId, combatantId, controllerSlotId, allegiance } = args;
  return {
    type: "set_controller",
    actorId,
    combatantId,
    controllerSlotId,
    ...(allegiance !== undefined ? { allegiance } : {}),
  };
}

/** Which half of a grant a GRANTS-row gesture moves: both (the slot-name chip), or one of them. */
export type GrantAxis = "both" | "control" | "view";

/** One slot's current grant on a combatant — CTRL is the co-list, EYES the view list. */
export function grantsOf(
  combatant: Pick<Combatant, "coControllerSlotIds" | "visibilityGrantSlotIds">,
  slotId: string,
): { control: boolean; view: boolean } {
  return {
    control: (combatant.coControllerSlotIds ?? []).includes(slotId),
    view: (combatant.visibilityGrantSlotIds ?? []).includes(slotId),
  };
}

/**
 * Toggle one slot's **grant** on the given combatant — CTRL (the co-controller list), EYES (the
 * visibility-grant list, `decisions/0179`), or both — leaving the owner of record and the side
 * untouched. Read-modify-write against the combatant's current lists.
 *
 * `"both"` is the slot-name chip: it turns both halves ON unless both are already on, in which
 * case it turns both OFF. The GM wants the two together nine times out of ten (the developer's
 * ruling), so the one-click gesture sets them in sync; CTRL and EYES toggle independently for the
 * tenth.
 *
 * Only the list(s) the gesture moves are sent: `set_controller` reads an omitted list as "leave
 * alone", so a CTRL toggle can never disturb EYES and vice versa. `controllerSlotId` is re-sent
 * unchanged because the command's own `controllerSlotId` is **mandatory** — there is no "leave the
 * owner alone" on the wire, so the honest way to say it is to restate the current value.
 */
export function toggleGrantsCommand(args: {
  actorId: string;
  combatant: Pick<Combatant, "id" | "controllerSlotId" | "coControllerSlotIds" | "visibilityGrantSlotIds">;
  slotId: string;
  axis: GrantAxis;
}): SetControllerCommand {
  const { actorId, combatant, slotId, axis } = args;
  const has = grantsOf(combatant, slotId);
  const both = !(has.control && has.view);
  const set = (list: string[] | undefined, on: boolean): string[] => {
    const current = list ?? [];
    if (!on) return current.filter((s) => s !== slotId);
    return current.includes(slotId) ? current : [...current, slotId];
  };
  const control = axis === "both" ? both : axis === "control" ? !has.control : undefined;
  const view = axis === "both" ? both : axis === "view" ? !has.view : undefined;
  return {
    type: "set_controller",
    actorId,
    combatantId: combatant.id,
    controllerSlotId: combatant.controllerSlotId ?? null,
    ...(control === undefined ? {} : { coControllerSlotIds: set(combatant.coControllerSlotIds, control) }),
    ...(view === undefined ? {} : { visibilityGrantSlotIds: set(combatant.visibilityGrantSlotIds, view) }),
  };
}

/**
 * Does assigning this combatant to a **player** leave it activating in the hostile bucket?
 *
 * The browser twin of the CLI's `--slot`-with-no-`--side` guard.
 * `SetControllerCommand` carries control and side together precisely so a GM cannot
 * strand a player with a unit that never gets a turn on their side — but `allegiance` is
 * optional on the command (load-bearing for the lossless inverse), so a surface that only sets
 * the owner can silently reproduce that incoherence.
 *
 * The CLI answers this by REFUSING (a usage error naming the fix). A UI should not refuse — the
 * GM can see the side and may genuinely want a player-driven hostile — so this returns the fact
 * and lets the panel surface it as a warning beside the control.
 */
export function assignmentStrandsUnit(args: {
  /** Only the side is read, so the DEPLOY picker can ask about a unit that does not exist yet. */
  combatant: { base: Pick<Combatant["base"], "allegiance"> };
  /** The slot about to gain control, or `null` for the GM. */
  slotId: string | null;
}): boolean {
  return args.slotId !== null && args.combatant.base.allegiance === "npc";
}

/**
 * The display list of controllers for a combatant — owner of record first, then co-drivers,
 * which is exactly {@link controllersOf}'s order. Re-exported through this module so the panel
 * has one import surface and cannot accidentally hand-roll the ordering.
 */
export { controllersOf };
